import { FC, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import theme from "@/theme";
import HeartSvg from "@/assets/svg/HeartSvg";
import ZapsSvg from "@/assets/svg/ZapsSvg";
import Polygon from "@/assets/svg/Polygon";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { useTopEarnersQuery } from "@/store/api/leaderboardApi";
import { useAppSelector } from "@/hooks/useAppSelector";
import { formatEarnings } from "@/helpers/formatEarnings";
import { styles } from "../stylesLeaderboard";

export type EarningsFilter = "Total Earned" | "Likes Earnings" | "Zaps Earnings";

type EarningsType = "total" | "likes" | "zaps";

type Earner = {
  uid: string;
  username: string;
  avatarUrl?: string | null;
  earnings: number;
};

const typeMap: Record<EarningsFilter, EarningsType> = {
  "Total Earned": "total",
  "Likes Earnings": "likes",
  "Zaps Earnings": "zaps",
};

const MAX_EARNERS = 100;

interface IEarnersTableProps {
  earningsFilter: EarningsFilter;
  order: string;
  setOrder: (order: string) => void;
}

const HIGHLIGHT_BG = "rgba(139, 197, 244, 0.1)";

const EarnersTable: FC<IEarnersTableProps> = ({ earningsFilter, order, setOrder }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAppSelector((state) => state.auth);

  const [earners, setEarners] = useState<Earner[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);

  const queryParams = useMemo(
    () => ({ type: typeMap[earningsFilter], page }),
    [earningsFilter, page]
  );

  const { data, isLoading } = useTopEarnersQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setEarners([]);
  }, [earningsFilter]);

  useEffect(() => {
    if (!data?.items) return;

    const mapped: Earner[] = data.items.map((u: Partial<Earner>) => ({
      uid: u.uid ?? "",
      username: u.username ?? "",
      avatarUrl: u.avatarUrl ?? null,
      earnings: u.earnings ?? 0,
    }));

    setEarners((prev) => {
      const merged =
        data.page === 1
          ? mapped
          : [...prev, ...mapped.filter((m) => !prev.some((e) => e.uid === m.uid))];
      return merged.slice(0, MAX_EARNERS);
    });

    const total = data.total ?? 0;
    const limit = data.limit ?? mapped.length;
    const loadedCount = (data.page === 1 ? 0 : earners.length) + mapped.length;
    setHasMore(mapped.length === limit && loadedCount < total && loadedCount < MAX_EARNERS);
    setLoadingPage(false);
  }, [data]);

  const sortedEarners = useMemo(
    () =>
      [...earners].sort((a, b) =>
        order === "asc" ? a.earnings - b.earnings : b.earnings - a.earnings
      ),
    [earners, order]
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loadingPage && !isLoading) {
      setLoadingPage(true);
      setPage((prev) => prev + 1);
    }
  };

  const columnLabel =
    earningsFilter === "Total Earned" ? "Earnings" : earningsFilter;

  const columnIcon =
    earningsFilter === "Likes Earnings" ? (
      <HeartSvg height={20} width={20} />
    ) : earningsFilter === "Zaps Earnings" ? (
      <ZapsSvg />
    ) : null;

  const handleSortClick = () => {
    setOrder(order === "asc" ? "desc" : "asc");
  };

  const hideAsc = order === "asc";
  const hideDesc = !hideAsc;

  const handleClick = (uid: string) => {
    navigate(`/profile/${uid}`);
  };

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Box sx={{ borderRadius: "0.5rem" }}>
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead sx={{ backgroundColor: "#F5F9FE", borderRadius: "8px" }}>
            <TableRow sx={{ display: "flex" }}>
              <TableCell
                align="center"
                sx={[
                  styles.tableCellHead,
                  { width: "5rem", justifyContent: "center", cursor: "default" },
                ]}
              >
                #
              </TableCell>
              <TableCell
                sx={[styles.tableCellHead, { width: "100%", flex: "1 0 0", cursor: "default" }]}
              >
                User
              </TableCell>
              <TableCell
                sx={[styles.tableCellHead, { width: "200px", flexShrink: 0 }]}
                onClick={handleSortClick}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap="2px"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {columnIcon}
                  {columnLabel}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "20px",
                      height: "20px",
                      padding: "1px",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        marginBottom: "1px",
                        display: "flex",
                        flexDirection: "column",
                        width: "12px",
                        height: "18px",
                        gap: "2px",
                      }}
                    >
                      <Box sx={{ display: "contents" }}>
                        <Polygon color={hideAsc ? theme.palette.custom.mainBlue : "#2A2D31"} />
                      </Box>
                      <Box sx={{ display: "contents" }}>
                        <Polygon
                          flipped
                          color={hideDesc ? theme.palette.custom.mainBlue : "#2A2D31"}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <Box
            sx={{
              height: "calc(100vh - 280px)",
              overflowY: "auto",
              display: "block",
            }}
            onScroll={handleScroll}
          >
            {isLoading && earners.length === 0 ? (
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 4 }}>
                <CustomLoader size={48} loading />
              </Box>
            ) : (
              <TableBody sx={{ display: "block" }}>
                {sortedEarners.map((earner, index) => {
                  const isCurrentUser = !!authUser?.uid && earner.uid === authUser.uid;

                  return (
                    <TableRow
                      key={earner.uid}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        backgroundColor: isCurrentUser ? HIGHLIGHT_BG : "transparent",
                      }}
                    >
                      <TableCell
                        sx={[
                          styles.tableCellBody,
                          { width: "5rem", justifyContent: "center", bgcolor: "transparent" },
                        ]}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={500}
                          lineHeight="145%"
                          color={theme.palette.custom.blueGray900}
                        >
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCellBody,
                          { width: "100%", flex: "1 0 0", bgcolor: "transparent" },
                        ]}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="0.75rem"
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleClick(earner.uid)}
                        >
                          <Avatar
                            src={earner.avatarUrl ?? undefined}
                            alt={earner.username}
                            sx={{ width: 40, height: 40 }}
                          />
                          {isCurrentUser ? (
                            <Box display="flex" alignItems="center" gap="0.5rem">
                              <Typography
                                variant="h6"
                                fontWeight={500}
                                lineHeight="145%"
                                color={theme.palette.custom.blueGray900}
                              >
                                @{earner.username}
                              </Typography>
                              <Box
                                sx={{
                                  backgroundColor: theme.palette.custom.mainBlue,
                                  borderRadius: "4px",
                                  padding: "2px 8px",
                                }}
                              >
                                <Typography
                                  fontSize="12px"
                                  fontWeight={600}
                                  lineHeight="16px"
                                  color="#fff"
                                >
                                  You
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              variant="h6"
                              fontWeight={500}
                              lineHeight="145%"
                              color={theme.palette.custom.blueGray900}
                            >
                              @{earner.username}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCellBody,
                          { width: "200px", flexShrink: 0, bgcolor: "transparent" },
                        ]}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={500}
                          lineHeight="145%"
                          color={theme.palette.custom.blueGray900}
                        >
                          {formatEarnings(earner.earnings)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {loadingPage && (
                  <TableRow sx={{ display: "flex", justifyContent: "center" }}>
                    <TableCell
                      sx={{ display: "flex", justifyContent: "center", py: 2, borderBottom: "none" }}
                    >
                      <CustomLoader size={32} loading />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Box>
        </Table>
      </Box>
    </TableContainer>
  );
};

export default EarnersTable;
